import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect as unistoreConnect } from '@app/store/unistore/combined/react';
import userActions from '@app/actions/user'; // Assuming general user actions, might need specific sso actions
import { LoadingDots } from '@app/ui';
import sportsApi from '@app/utils/sportsApi'; // Assuming this is your API utility
import { Notification } from '@app/components/notifications/Notifications';

// TODO: Define proper types for props if not using 'any' from userActions
type FreshdeskSSOForwardPageProps = {
    user: any;
    sendNotification: (
        text: string,
        notification: Notification['options'],
    ) => void;
    // Add specific action for freshdesk sso if created
    // initiateFreshdeskSSO: (params: { client_id: string; state: string; nonce: string }) => Promise<string | null>;
};

const FreshdeskSSOForwardPage = ({
    user,
    sendNotification,
}: FreshdeskSSOForwardPageProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const body = document.querySelector('body');
        if (body) {
            body.classList.add('body-gradient-theme');
        }
        return () => {
            if (body) {
                body.classList.remove('body-gradient-theme');
            }
        };
    }, []);

    useEffect(() => {
        const processFreshdeskSSO = async () => {
            setIsLoading(true);
            setError(null);
            const searchParams = new URLSearchParams(location.search);
            const clientId = searchParams.get('client_id');
            const state = searchParams.get('state');
            const nonce = searchParams.get('nonce');

            if (!clientId || !state || !nonce) {
                setError('Missing required parameters from Freshdesk.');
                sendNotification(
                    'SSO initiation failed: Missing parameters from Freshdesk.',
                    {
                        context: 'error',
                    },
                );
                setIsLoading(false);
                // Optionally redirect to a generic login or error page
                // navigate('/login');
                return;
            }

            try {
                // This is where you'd call your backend.
                // The backend should authenticate the user (if not already),
                // generate the JWT, and return the full redirect URL for Freshdesk.
                const response = await sportsApi
                    .url('/freshdesk/sso') // New backend endpoint
                    .query({ client_id: clientId, state, nonce })
                    .get()
                    .json<{ redirectUrl?: string; error?: string }>();

                if (response.error || !response.redirectUrl) {
                    setError(
                        response.error ||
                            'Failed to get redirect URL from backend.',
                    );
                    sendNotification(
                        response.error ||
                            'SSO failed: Could not retrieve redirect information.',
                        {
                            context: 'error',
                        },
                    );
                    setIsLoading(false);
                    // navigate('/login'); // Or an error page
                    return;
                }

                // Redirect to Freshdesk with the id_token
                window.location.href = response.redirectUrl;
            } catch (e: any) {
                console.error('Freshdesk SSO processing error:', e);
                setError('An unexpected error occurred during SSO processing.');
                sendNotification(
                    e.message ||
                        'An unexpected error occurred during Freshdesk SSO.',
                    {
                        context: 'error',
                    },
                );
                setIsLoading(false);
                // navigate('/login'); // Or an error page
            }
        };

        if (!user.isLoggedIn) {
            // User is not logged in. Redirect to login page, passing the current location.
            // The login page, upon success, should redirect back to location.state.from.
            setIsLoading(false); // Stop loading this page as we are redirecting
            navigate('/login', { state: { from: location }, replace: true });
            return; // Stop further execution in this effect
        }

        // User is logged in, proceed with SSO processing.
        processFreshdeskSSO();
    }, [location, user.isLoggedIn, sendNotification, navigate]);

    if (isLoading) {
        return (
            <main>
                <h1 className="gds-text--header-lg -color-tx-white">
                    Processing Freshdesk Single Sign-On
                </h1>
                <p className="-color-tx-white">Please wait a moment...</p>
                <section>
                    <LoadingDots className="-m-t-5" context="white" />
                </section>
            </main>
        );
    }

    if (error) {
        return (
            <main>
                <h1 className="gds-text--header-lg -color-tx-white">
                    Freshdesk SSO Error
                </h1>
                <p className="-color-tx-white">{error}</p>
                {/* Optionally provide a button to retry or go to login */}
            </main>
        );
    }

    // Should have redirected or shown an error by now.
    return null;
};

// Ensure userActions and other dependencies are correctly typed and connected
// For example, if sendNotification comes from a different part of your store:
// export default unistoreConnect(['user', 'notifications'], { ...userActions, ...notificationActions })(FreshdeskSSOForwardPage);
// For now, using a simplified connection based on the Zendesk example
export default unistoreConnect(['user'], userActions)(FreshdeskSSOForwardPage);
